// Formatação de data/hora SEMPRE no fuso de New York (America/New_York) e no
// formato de data mm/dd/yyyy. O sistema inteiro deve exibir assim.
//
// O backend grava datetime com fuso (ex: 2026-08-12T19:03:00-04:00). Quando o
// valor vem "naive" (sem marca de fuso), tratamos como UTC antes de converter
// para NY. Datas "só data" (YYYY-MM-DD) NÃO sofrem conversão de fuso (senão o
// dia mudaria); apenas reordenamos para MM/DD/YYYY.

const NY_TZ = 'America/New_York';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const pad2 = (n) => String(n).padStart(2, '0');

// Normaliza uma string "só data" (sem hora) para MM/DD/YYYY. Cobre formatos
// legados: YYYY-MM-DD, DD-MM-YYYY, MM-DD-YYYY, DD/MM/YYYY, MM/DD/YYYY e ano de
// 2 dígitos. Retorna null se não parecer uma data "só data".
const normalizeDateOnly = (raw) => {
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);        // ISO
  if (m) return `${pad2(m[2])}/${pad2(m[3])}/${m[1]}`;
  m = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);    // dash ou barra
  if (m) {
    let a = parseInt(m[1]), b = parseInt(m[2]), y = m[3];
    if (y.length === 2) y = (parseInt(y) < 50 ? '20' : '19') + y;
    let month, day;
    if (a > 12 && b <= 12) { day = a; month = b; }           // claramente DD?/MM
    else { month = a; day = b; }                             // padrão MM/DD (convenção do app)
    return `${pad2(month)}/${pad2(day)}/${y}`;
  }
  return null;
};

const parseToDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  if (DATE_ONLY.test(raw)) {
    // Só data: monta ao meio-dia UTC pra nunca "virar" o dia ao formatar em NY.
    const [y, m, d] = raw.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  }
  const hasTz = /[zZ]$|[+\-]\d{2}:?\d{2}$/.test(raw);
  const d = new Date(hasTz ? raw : raw + 'Z');
  return isNaN(d.getTime()) ? null : d;
};

const lower = (s) => s.replace(/\s(AM|PM)/, (m, x) => x.toLowerCase());

// MM/DD/YYYY
export const formatNyDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const raw = String(value).trim();
  const dOnly = normalizeDateOnly(raw);
  if (dOnly) return dOnly;
  const d = parseToDate(value);
  if (!d) return raw;
  return d.toLocaleDateString('en-US', { timeZone: NY_TZ, month: '2-digit', day: '2-digit', year: 'numeric' });
};

// MM/DD/YYYY, h:mm[:ss]am/pm
export const formatNyDateTime = (value, { seconds = false } = {}) => {
  if (value === null || value === undefined || value === '') return '';
  const d = parseToDate(value);
  if (!d) return String(value);
  const opts = { timeZone: NY_TZ, month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
  if (seconds) opts.second = '2-digit';
  return lower(d.toLocaleString('en-US', opts));
};

// h:mm[:ss]am/pm (só hora, em NY)
export const formatNyTime = (value, { seconds = false } = {}) => {
  const d = parseToDate(value);
  if (!d) return '';
  const opts = { timeZone: NY_TZ, hour: 'numeric', minute: '2-digit', hour12: true };
  if (seconds) opts.second = '2-digit';
  return lower(d.toLocaleTimeString('en-US', opts));
};

// "13 de agosto de 2026" -> mantém nomes em PT, mas no fuso de NY. Usado só
// onde a UI quer o nome do mês/dia da semana por extenso.
export const formatNyLong = (value, opts = { weekday: undefined, day: 'numeric', month: 'long', year: 'numeric' }) => {
  const d = parseToDate(value);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR', { timeZone: NY_TZ, ...opts });
};

export default formatNyDateTime;
